export interface SEOMetadata {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
}

export const updateMetaTags = (metadata: SEOMetadata) => {
  const defaults = {
    title: 'Bardus AI - AI-Powered Sound Effects Generator for Game Developers',
    description: 'Generate high-quality sound effects instantly with AI. Bardus AI helps game developers create custom audio assets quickly and affordably.',
    image: 'https://bardus.ai/og-image.png',
    url: window.location.href,
    type: 'website'
  };

  const meta = { ...defaults, ...metadata };

  // Update title
  document.title = meta.title;
  updateMetaTag('property', 'og:title', meta.title);
  updateMetaTag('property', 'twitter:title', meta.title);
  
  // Update description
  updateMetaTag('name', 'description', meta.description);
  updateMetaTag('property', 'og:description', meta.description);
  updateMetaTag('property', 'twitter:description', meta.description);
  
  // Update image
  updateMetaTag('property', 'og:image', meta.image);
  updateMetaTag('property', 'twitter:image', meta.image);
  
  // Update URL
  updateMetaTag('property', 'og:url', meta.url);
  updateMetaTag('property', 'twitter:url', meta.url);
  updateCanonicalLink(meta.url);
  
  // Update type
  updateMetaTag('property', 'og:type', meta.type);
};

function updateMetaTag(attr: string, attrValue: string, content: string) {
  let element = document.querySelector(`meta[${attr}="${attrValue}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, attrValue);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

function updateCanonicalLink(url: string) {
  let link = document.querySelector('link[rel="canonical"]');
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', url);
}

// Route-specific SEO configurations
export const routeSEO: Record<string, SEOMetadata> = {
  '/': {
    title: 'Bardus AI - AI-Powered Sound Effects Generator for Game Developers',
    description: 'Generate high-quality sound effects instantly with AI. Perfect for game developers who need custom audio assets quickly and affordably.'
  },
  '/projects': {
    title: 'My Projects | Bardus AI',
    description: 'Manage your sound effect projects and organize your AI-generated audio assets in one place.'
  },
  '/generate': {
    title: 'Generate Sound Effects | Bardus AI',
    description: 'Create custom sound effects with AI. Describe any sound and get high-quality audio files instantly.'
  },
  '/settings': {
    title: 'Settings | Bardus AI',
    description: 'Configure your Bardus AI account settings and preferences.'
  },
  '/pricing': {
    title: 'Pricing | Bardus AI - Affordable Sound Generation',
    description: 'Simple, transparent pricing for AI sound generation. Start free and scale as you grow.'
  },
  '/about': {
    title: 'About Us | Bardus AI',
    description: 'Learn about Bardus AI and our mission to empower game developers with AI-powered sound generation.'
  }
};

// Helper to generate structured data for different page types
export const generateStructuredData = (type: 'homepage' | 'product' | 'faq') => {
  const baseData = {
    '@context': 'https://schema.org',
    url: 'https://bardus.ai'
  };

  switch (type) {
    case 'homepage':
      return {
        ...baseData,
        '@type': 'WebSite',
        name: 'Bardus AI',
        description: 'AI-powered sound effects generator for game developers',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://bardus.ai/search?q={search_term_string}',
          'query-input': 'required name=search_term_string'
        }
      };
    
    case 'product':
      return {
        ...baseData,
        '@type': 'Product',
        name: 'Bardus AI Sound Generator',
        description: 'AI-powered sound effects generation platform',
        brand: {
          '@type': 'Brand',
          name: 'Bardus AI'
        },
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: '0',
          highPrice: '99'
        }
      };
    
    case 'faq':
      return {
        ...baseData,
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is Bardus AI?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Bardus AI is an AI-powered platform that helps game developers generate high-quality sound effects instantly.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does the AI sound generation work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Simply describe the sound you need, and our AI will generate a high-quality audio file matching your description.'
            }
          },
          {
            '@type': 'Question',
            name: 'What audio formats are supported?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Bardus AI generates sound effects in MP3 format, perfect for game development and compatible with all major game engines.'
            }
          }
        ]
      };
    
    default:
      return baseData;
  }
};